import { formatISO } from "date-fns";
import { from, interval, Subject, timer } from "rxjs";
import { concatMap, map, switchMap, take, takeUntil } from "rxjs/operators";
import { lookupCashierId, lookupCashierUserName, lookupDeviceId, lookupStoreInfo } from "./browser";
import { catchJSONStringifyErrors, stringifyObjects } from "./stringify";
const REMOTE_LOG_INTERVAL = 500;
const ERROR_BACKOFF_BASE = 2;
const BATCH_REQUEST_MAX_SIZE = 100000;
const BATCH_REQUEST_MAX_COUNT = 500;
const __lifetime = new Subject();
__lifetime.pipe(take(1)).subscribe(() => {
  __lifetime.complete();
  outgoingRequestSubject.complete();
  incomingLogSubject.complete();
});
const earlyEvents = [];
const incomingLogSubject = new Subject();
const outgoingRequestSubject = new Subject();
let consecutiveErrors = 0;
let messageBatch = "";
let remoteLoggerHasInitialized = false;
let RemoteLoggerConfig;
export function initializeRemoteLogQueue({
  appVersion,
  namespace,
  commitSHA,
  remoteURL
}) {
  RemoteLoggerConfig = {
    appVersion,
    namespace,
    commitSHA,
    remoteURL
  };
  incomingLogSubject.pipe(map(incomingEvent => formatForRemoteLogger(incomingEvent))).subscribe(sendOrQueueIncomingMessage());
  interval(REMOTE_LOG_INTERVAL).pipe(takeUntil(__lifetime)).subscribe(sendBatchIfExists());
  outgoingRequestSubject.pipe(concatMap(sendRemoteRequest)).subscribe(resetErrorCountOnSuccess);
  remoteLoggerHasInitialized = true;
  from(earlyEvents).subscribe(incomingLogSubject.next.bind(incomingLogSubject));
  return __lifetime.next.bind(__lifetime);
}
export function sendToRemoteLoggerQueue(incomingEvent) {
  if (!remoteLoggerHasInitialized) {
    earlyEvents.push(incomingEvent);
    return;
  }
  incomingLogSubject.next(incomingEvent);
}
export function sendToRemoteRequestQueue(outgoingRequest) {
  outgoingRequestSubject.next(outgoingRequest);
}
function sendOrQueueIncomingMessage() {
  return nextMessage => {
    const nextMessageString = stringifyObjects(nextMessage);
    if ((messageBatch + nextMessageString).length + 1 >= BATCH_REQUEST_MAX_SIZE || JSON.parse(`[${messageBatch}]`).length >= BATCH_REQUEST_MAX_COUNT) {
      sendToRemoteRequestQueue(messageBatch);
      messageBatch = stringifyObjects(nextMessage);
      if (messageBatch.length > BATCH_REQUEST_MAX_SIZE) {
        sendToRemoteRequestQueue(messageBatch);
        messageBatch = "";
      }
    } else {
      if (messageBatch.length > 0) {
        messageBatch += ",";
      }
      messageBatch += stringifyObjects(nextMessage);
    }
    if (nextMessage.severity === "error") {
      sendToRemoteRequestQueue(messageBatch);
      messageBatch = "";
    }
  };
}
function sendBatchIfExists() {
  return () => {
    if (messageBatch.length) {
      sendToRemoteRequestQueue(messageBatch);
      messageBatch = "";
    }
  };
}
function sendRemoteRequest(eventData) {
  if (consecutiveErrors > 0) {
    const delay = Math.pow(ERROR_BACKOFF_BASE, Math.min(consecutiveErrors - 1, 4)) * 1000;
    return timer(delay).pipe(switchMap(() => sendRequest(eventData)));
  }
  return sendRequest(eventData);
  function sendRequest(eventData) {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", RemoteLoggerConfig.remoteURL, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    const obsRequest = new Subject();
    xhr.addEventListener("load", obsRequest.next.bind(obsRequest));
    xhr.addEventListener("loadend", obsRequest.complete.bind(obsRequest));
    xhr.addEventListener("error", error => {
      consecutiveErrors += 1;
      sendToRemoteRequestQueue(eventData);
      sendToRemoteLoggerQueue({
        process: "QQUtil/Logger/remote",
        action: "sendRemoteRequest",
        message: "Error sending to remote logger",
        context: [{
          error
        }],
        severity: "warn",
        orderId: undefined,
        vehicleKey: undefined
      });
    });
    xhr.send(`[${eventData}]`);
    return obsRequest.asObservable();
  }
}
function resetErrorCountOnSuccess(success) {
  if (success) {
    consecutiveErrors = 0;
  }
}
function formatForRemoteLogger({
  process,
  action,
  message,
  context,
  severity,
  orderId,
  vehicleKey
}) {
  const environmentData = Object.assign({
    timestamp: formatISO(new Date()),
    buildVersion: RemoteLoggerConfig === null || RemoteLoggerConfig === void 0 ? void 0 : RemoteLoggerConfig.appVersion,
    namespace: RemoteLoggerConfig === null || RemoteLoggerConfig === void 0 ? void 0 : RemoteLoggerConfig.namespace,
    commitSHA: RemoteLoggerConfig === null || RemoteLoggerConfig === void 0 ? void 0 : RemoteLoggerConfig.commitSHA,
    employeeId: lookupCashierId() || "No employee ID in session",
    employeeUsername: lookupCashierUserName() || "No username in session",
    deviceId: lookupDeviceId()
  }, lookupStoreInfo());
  let contextData;
  switch (context && context.length) {
    case 0:
      contextData = {};
      break;
    case 1:
      contextData = {
        context: context[0]
      };
      break;
    default:
      contextData = {
        context
      };
      break;
  }
  contextData = catchJSONStringifyErrors(Object.assign(Object.assign(Object.assign({}, environmentData), contextData), {
    severity,
    Process: process,
    Action: action,
    orderId,
    vehicleKey
  }));
  return Object.assign({
    message: catchJSONStringifyErrors(message)
  }, typeof contextData === "string" ? {
    contextData
  } : contextData);
}
